import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { TEST_IDS } from "@/constants/testIds";
import { Radio, FilmStrip, BookBookmark, SignOut, SignIn, MusicNotes, Compass, MagnifyingGlass } from "@phosphor-icons/react";

const navItems = [
  { to: "/", label: "Home", testId: TEST_IDS.nav.home, end: true },
  { to: "/radios", label: "Radio", testId: TEST_IDS.nav.radios, icon: Radio },
  { to: "/music", label: "Music", testId: TEST_IDS.nav.music, icon: MusicNotes },
  { to: "/videos", label: "Videos", testId: TEST_IDS.nav.videos, icon: FilmStrip },
  { to: "/discover", label: "Discover", testId: TEST_IDS.nav.discover, icon: Compass },
  { to: "/library", label: "Library", testId: TEST_IDS.nav.library, icon: BookBookmark },
];

function RechoLogo() {
  return (
    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-[#ff7a18] via-[#ff4d1d] to-[#c9220f] flex items-center justify-center shadow-[0_0_28px_rgba(255,77,29,.38)] shrink-0 border border-white/10">
      <div className="flex items-end gap-[3px] h-6">
        {[14, 22, 17, 25, 11].map((h, i) => (
          <span key={i} className="w-[3px] rounded-full bg-white/95" style={{ height: h }} />
        ))}
      </div>
    </div>
  );
}

export default function Navbar() {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      style={{ top: "env(safe-area-inset-top)" }}
      className="sticky z-40 border-b border-white/10 bg-[#05070d]/75 backdrop-blur-2xl shadow-[0_18px_64px_rgba(0,0,0,.34)]"
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-4">
        <Link to="/" data-testid={TEST_IDS.nav.brand} className="flex items-center gap-3 group min-w-0">
          <RechoLogo />
          <div className="min-w-0">
            <div className="font-serif-display text-2xl sm:text-3xl font-bold leading-none tracking-tight truncate text-white">
              Recho <span className="text-[#ff4d1d]">Music</span>
            </div>
            <div className="hidden sm:block font-mono text-[9px] uppercase tracking-[0.22em] text-slate-300/75 mt-1">by NeurAlpha · World Audio</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.025] p-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testId}
              className={({ isActive }) =>
                `relative px-4 xl:px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.14em] transition-all ${
                  isActive
                    ? "text-white bg-white/[0.065] shadow-[inset_0_-2px_0_rgba(255,77,29,.95)]"
                    : "text-slate-300/75 hover:text-white hover:bg-white/[0.05]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <button
            type="button"
            onClick={() => navigate("/discover")}
            className="hidden md:flex min-h-[44px] w-12 xl:w-40 items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.035] px-3 text-slate-300/100 hover:text-white hover:border-cyan-300/40 hover:bg-cyan-300/10 transition-colors"
            aria-label="Search"
          >
            <MagnifyingGlass size={18} />
            <span className="hidden xl:inline font-sans text-sm">Search</span>
          </button>

          {user ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/library")}
                className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-2xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] transition-colors max-w-[180px]"
                title={user.email}
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-white/20" />
                ) : (
                  <span className="w-7 h-7 rounded-full bg-[#ff4d1d] text-white flex items-center justify-center text-xs font-bold">
                    {user.name?.[0] || "U"}
                  </span>
                )}
                <span className="font-sans text-sm text-slate-200 truncate">{user.name || "Library"}</span>
              </button>
              <button
                onClick={logout}
                className="min-h-[44px] px-3 sm:px-4 rounded-2xl border border-white/10 bg-white/[0.04] text-slate-200 hover:text-white hover:border-[#ff4d1d]/60 transition-colors flex items-center gap-1.5"
                aria-label="Sign out"
              >
                <SignOut size={16} /> <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.16em]">Exit</span>
              </button>
            </div>
          ) : (
            <button
              data-testid={TEST_IDS.nav.login}
              onClick={login}
              className="neon-button min-h-[44px] px-4 sm:px-6 py-2.5 font-mono text-[10px] sm:text-xs uppercase tracking-[0.16em] flex items-center gap-2"
            >
              <SignIn size={15} /> Sign in
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
